import { useState } from "react";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { useI18n } from "../contexts/I18nContext";

/**
 * Compact row actions for list/grid items. Renders an inline kebab menu with
 * Edit + Delete. The parent controls editing via `onEdit(item)` callback and
 * deletion via `onDelete(item)` (returning a promise).
 */
export default function RowActions({ item, onEdit, onDelete, testIdPrefix = "row" }) {
  const { t } = useI18n();
  const [confirm, setConfirm] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`${testIdPrefix}-actions-${item.id}`}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onEdit && (
            <DropdownMenuItem onClick={() => onEdit(item)} data-testid={`${testIdPrefix}-edit-${item.id}`}>
              <Pencil className="w-4 h-4 me-2" /> {t("edit")}
            </DropdownMenuItem>
          )}
          {onDelete && (
            <DropdownMenuItem onClick={() => setConfirm(true)} className="text-destructive" data-testid={`${testIdPrefix}-delete-${item.id}`}>
              <Trash2 className="w-4 h-4 me-2" /> {t("delete")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>—</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => { setConfirm(false); await onDelete(item); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid={`${testIdPrefix}-confirm-delete-${item.id}`}
            >
              {t("yes_delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
